import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import useAuthorityConfiguration from "../../../../../api/utilities/useAuthorityConfiguration.js";
import {ErpCalculationApi} from "../../../../../api/authority/generated/index";
import {useTranslation} from "react-i18next";
import SeeMore from "../../../../../components/SeeMore.jsx";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../../../../api/utilities/translateErrorMessage.js";
import Toggle from "../../../../../components/Toggle.jsx";
import {FilterContext} from "../../../../../components/filters/DashboardFilterContextProvider.jsx";
import {useRenderCurrencyValue} from "../../../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../../../utils/useUserSettings.js";
import DataGrid2 from "../../../../../components/datagrid/DataGrid2.jsx";
import Pagination from "../../../../../components/Pagination.jsx";
import Button from "../../../../../components/Button.jsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {fas} from "@fortawesome/free-solid-svg-icons";

const Mode = {
    Turnover: 'turnover',
    Amount: 'amount',
};

const ItemsPerPage = 20;
const TopLimitOptions = [20, 50, 100];

export default function ProductPerformance({project}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();
    let renderCurrencyValue = useRenderCurrencyValue();

    let {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);

    let [data, setData] = useState(null);
    let [dataLoading, setDataLoading] = useState(true);
    let authorityConfiguration = useAuthorityConfiguration();

    let [userSettings] = useUserSettings();
    let [page, setPage] = useState(1);
    let [topLimit, setTopLimit] = useState(20);
    let [searchDraft, setSearchDraft] = useState('');
    let [searchQuery, setSearchQuery] = useState('');
    let sinceKey = since.format('YYYY-MM-DD');
    let untilKey = until.format('YYYY-MM-DD');
    let centresKey = centresAllowListed.join(',');
    let domainsKey = domainsAllowListed.join(',');
    let baseRequestKey = useMemo(() => [
        project.id,
        sinceKey,
        untilKey,
        centresKey,
        domainsKey,
        searchQuery,
    ].join('|'), [centresKey, domainsKey, project.id, searchQuery, sinceKey, untilKey]);
    let previousBaseRequestKey = useRef(baseRequestKey);

    useEffect(() => {
        if (previousBaseRequestKey.current !== baseRequestKey && page !== 1) {
            previousBaseRequestKey.current = baseRequestKey;
            setPage(1);
            return;
        }

        previousBaseRequestKey.current = baseRequestKey;

        let active = true;
        setDataLoading(true);
        setData(null);

        new ErpCalculationApi(authorityConfiguration)
            .productPerformance({
                projectId: project.id,
                productPerformanceFilter: {
                    since: sinceKey,
                    until: untilKey,
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                    limit: ItemsPerPage,
                    page: page,
                    search: searchQuery || undefined,
                }
            })
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
                setDataLoading(false);
            })
            .catch(e => {
                if (!active) {
                    return;
                }

                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [baseRequestKey, page]);

    let columns = [
        {
            name: 'productName',
            header: t('orders.product-name'),
            defaultFlex: 2,
            sortable: false,
        },
        {
            name: 'amount',
            header: t('orders.amount'),
            defaultFlex: 1,
            textAlign: 'end',
            sortable: false,
        },
        {
            name: 'turnover',
            header: t('dashboard.turnover'),
            defaultFlex: 2,
            textAlign: 'end',
            minWidth: 100,
            sortable: false,
            render: (input) => renderCurrencyValue(
                input.data.turnover,
                true,
            ),
        },
    ];

    let [mode, setMode] = useState(Mode.Amount);

    let dataSource = useMemo(() => {
        if (mode === Mode.Turnover && userSettings?.isMarketing === true) {
            return data?.topProductsByTurnoverGross ?? [];
        }

        if (mode === Mode.Turnover && userSettings?.isMarketing === false) {
            return data?.topProductsByTurnoverNet ?? [];
        }


        if (mode === Mode.Amount && userSettings?.isMarketing === true) {
            return data?.topProductsByAmountGross ?? [];
        }

        if (mode === Mode.Amount && userSettings?.isMarketing === false) {
            return data?.topProductsByAmountNet ?? [];
        }

        return [];
    }, [data, mode, userSettings]);

    let pagination = useMemo(() => {
        let currentPage = data?.page ?? page;
        let totalItems = Math.min(Math.max(0, data?.totalItems ?? 0), topLimit);
        let itemsPerPage = ItemsPerPage;
        let totalPages = Math.ceil(totalItems / itemsPerPage);

        if (totalPages <= 1) {
            return undefined;
        }

        return {
            firstItemIndex: totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1,
            firstPage: 1,
            hasNextPage: currentPage < totalPages,
            hasPreviousPage: currentPage > 1,
            itemCount: totalItems,
            itemsPerPage: itemsPerPage,
            lastItemIndex: Math.min(currentPage * itemsPerPage, totalItems),
            lastPage: totalPages,
            page: currentPage,
            pageCount: totalPages,
        };
    }, [data, page, topLimit]);

    let displayedDataSource = useMemo(() => {
        if (pagination === undefined) {
            return dataSource.slice(0, topLimit);
        }

        let allowedItemsOnPage = Math.max(0, pagination.lastItemIndex - pagination.firstItemIndex + 1);

        return dataSource.slice(0, allowedItemsOnPage);
    }, [dataSource, pagination, topLimit]);

    // noinspection JSUnresolvedReference
    return (
        <LoadingComponent
            loading={dataLoading || data === null}
            className={'col-span-12 sm:col-span-6 text-slate-600 dark:text-slate-400'}
        >
            <div
                className="comp__roundedBox inline-flex flex-col w-full rounded-sm text-sm bg-white dark:bg-slate-800 shadow-lg border border-slate-200 dark:border-slate-700">
                <div
                    className={'px-5 py-4 border-b border-slate-100 dark:border-slate-700 flex justify-between'}>
                    <div className={'flex items-center gap-2 font-semibold text-slate-800 dark:text-slate-100'}>
                        <span>{t('orders.top', {defaultValue: 'Top'})}</span>
                        <label htmlFor={`product-performance-limit-${project.id}`} className="sr-only">
                            {t('orders.top-products-limit', {defaultValue: 'Počet produktů'})}
                        </label>
                        <select
                            id={`product-performance-limit-${project.id}`}
                            className={'form-select py-1 pl-3 pr-8 text-sm'}
                            value={topLimit}
                            onChange={(event) => {
                                setPage(1);
                                setTopLimit(Number(event.target.value));
                            }}
                        >
                            {TopLimitOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </select>
                        <span>{t('orders.products', {defaultValue: 'produktů'})}</span>
                    </div>
                    <div className={'flex gap-2'}>
                        {t('orders.turnover')}
                        <Toggle enabled={mode === Mode.Amount} setEnabled={() => {
                            setMode(mode === Mode.Amount ? Mode.Turnover : Mode.Amount);
                        }}/>
                        {t('orders.amount')}
                    </div>
                </div>
                <div className={'px-5 pt-4'}>
                    <div className={'flex align gap-2'}>
                        <label htmlFor={`product-performance-search-${project.id}`} className="sr-only">
                            {t('search-call-to-action')}
                        </label>
                        <div className={'relative w-full'}>
                            <input
                                id={`product-performance-search-${project.id}`}
                                className="comp__roundedControl group form-input w-full bg-white pr-10 dark:bg-slate-800"
                                type="search"
                                value={searchDraft}
                                placeholder={t('searching') + '…'}
                                onChange={(e) => setSearchDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Enter') {
                                        return;
                                    }

                                    setPage(1);
                                    setSearchQuery(searchDraft.trim());
                                }}
                            />
                            {searchDraft !== '' && (
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-3 flex items-center text-slate-400 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                                    aria-label={t('clear', {defaultValue: 'Vymazat'})}
                                    onClick={() => {
                                        setPage(1);
                                        setSearchDraft('');
                                        setSearchQuery('');
                                    }}
                                >
                                    <FontAwesomeIcon icon={fas.faXmark}/>
                                </button>
                            )}
                        </div>
                        <Button
                            aria-label={t('search-call-to-action')}
                            onClick={() => {
                                setPage(1);
                                setSearchQuery(searchDraft.trim());
                            }}
                        >
                            <FontAwesomeIcon icon={fas.faMagnifyingGlass} className={'px-2'}/>
                        </Button>
                    </div>
                </div>
                <div className={'p-3'}>
                    <SeeMore heightClass={'h-[223px]'} active={dataSource.length >= 5}>
                        <DataGrid2
                            columns={columns}
                            dataSource={displayedDataSource}
                        />
                    </SeeMore>
                    {pagination && (
                        <Pagination
                            className={'mt-3'}
                            pagination={pagination}
                            onPageChange={setPage}
                        />
                    )}
                </div>
            </div>
        </LoadingComponent>
    );
}
